import React, { useEffect, useState } from 'react';

import UnallocatedExpensesIndexFilter, { EMPTY_FILTER } from './UnallocatedExpensesIndexFilter';
import UnallocatedExpensesIndexDepartmentBlock, { money, percentage } from './UnallocatedExpensesIndexRows';
import { countProjects, departmentList, filterDepartments, grandTotals, hasActiveFilter } from './unallocatedSummaryTree';

import { MARGIN_NO_DIVISOR } from 'Helpers/marginPercentage';

import Button from 'CommonComponents/Button';
import ExpandCollapseAllButton from 'CommonComponents/ExpandCollapseAllButton';
import LoadingSpinner from 'CommonComponents/LoadingSpinner';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import FileExportIcon from 'Assets/icons/file-export.svg';

const COLUMN_COUNT = 4;

/**
 * The export archive carries the thresholds through as query parameters, so the
 * file the user generates from there matches what they were looking at.
 */
const exportHref = (baseUrl, filter) => {
    if (!hasActiveFilter(filter)) {
        return baseUrl;
    }

    const params = new URLSearchParams();
    if (filter.minPercent !== '') params.set('min_percent', filter.minPercent);
    if (filter.minBalance !== '') params.set('min_balance', filter.minBalance);

    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${params}`;
};

export default function UnallocatedExpensesIndexTable(props) {
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState(false);
    const [filter, setFilter] = useState(EMPTY_FILTER);
    // Every group starts open; the map holds only the ones the user has closed.
    const [collapsed, setCollapsed] = useState({});
    const [allExpanded, setAllExpanded] = useState(true);

    useEffect(() => {
        setLoading(true);
        setFailed(false);

        axios
            .get(props.fetchUrl)
            .then((res) => setDepartments(departmentList(res.data)))
            .catch(() => setFailed(true))
            .finally(() => setLoading(false));
    }, [props.fetchUrl]);

    const toggle = (key) => setCollapsed((previous) => ({ ...previous, [key]: !previous[key] }));

    const toggleAll = () => {
        const next = !allExpanded;
        setAllExpanded(next);
        setCollapsed(
            next
                ? {}
                : Object.fromEntries(
                      departments.flatMap((department) => [
                          [`dep:${department.id}`, true],
                          ...department.costcenters.map((costcenter) => [`cc:${costcenter.id}`, true]),
                      ]),
                  ),
        );
    };

    const visible = filterDepartments(departments, filter);
    const grand = grandTotals(visible);
    const filtered = hasActiveFilter(filter);

    return (
        <div className="table__card">
            <div className="tw-flex tw-justify-between tw-items-start tw-mb-6">
                <div className="tw-flex tw-items-center tw-gap-x-3">
                    <h5 className="table__header !tw-mb-0">{$t('Unallocated Summary')}</h5>
                    {loading && (
                        <span className="tw-flex tw-items-center tw-gap-x-1.5 tw-text-xs tw-text-charcoal-50">
                            <LoadingSpinner size="xxs" ySpacing={false} />
                            {$t('Generating report')}…
                        </span>
                    )}
                    {!loading && !failed && countProjects(visible) > 0 && <ExpandCollapseAllButton expanded={allExpanded} onClick={toggleAll} />}
                </div>
                <div className="tw-flex tw-justify-end tw-items-center tw-gap-x-3">
                    <UnallocatedExpensesIndexFilter filter={filter} onApply={setFilter} />
                    <Button variant="primary" as="a" href={exportHref(props.exportUrl, filter)} data-action="navigate">
                        <FileExportIcon className="tw-w-3 tw-h-3" />
                        {$t('Export to file')}
                    </Button>
                </div>
            </div>
            <div className="inbud-table_wrapper">
                <table className="inbud-table">
                    <thead>
                        <tr>
                            <th>{$t('Department / Cost Centre / Project')}</th>
                            <th>{$t('Project Value')}</th>
                            <th>{$t('Unallocated Balance')}</th>
                            <th>{$t('Unallocated %')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && <TableRowSkeleton rows={10} columns={COLUMN_COUNT} />}
                        {!loading && failed && (
                            <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
                                <td colSpan={COLUMN_COUNT} className="tw-p-4">
                                    <span>{$t('The report could not be generated. Refresh the page to try again.')}</span>
                                </td>
                            </tr>
                        )}
                        {!loading && !failed && visible.length === 0 && (
                            <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
                                <td colSpan={COLUMN_COUNT} className="tw-p-4">
                                    {filtered ? (
                                        <span className="tw-flex tw-items-center tw-gap-x-3">
                                            {$t('No projects match the filter.')}
                                            <button
                                                type="button"
                                                className="tw-border-0 tw-bg-transparent tw-p-0 tw-font-medium tw-text-blue tw-underline"
                                                onClick={() => setFilter(EMPTY_FILTER)}
                                            >
                                                {$t('Clear filter')}
                                            </button>
                                        </span>
                                    ) : (
                                        // Every project is listed, allocated or not, so an
                                        // empty result now means the budget has no projects
                                        // at all rather than nothing left unallocated.
                                        <span>{$t('There are no projects in this budget yet.')}</span>
                                    )}
                                </td>
                            </tr>
                        )}
                        {!loading &&
                            !failed &&
                            visible.map((department) => (
                                <UnallocatedExpensesIndexDepartmentBlock
                                    key={department.id}
                                    department={department}
                                    collapsed={collapsed}
                                    onToggleDepartment={(id) => toggle(`dep:${id}`)}
                                    onToggleCostcentre={(id) => toggle(`cc:${id}`)}
                                />
                            ))}
                    </tbody>
                    {!loading && !failed && visible.length > 0 && (
                        <tfoot>
                            <tr>
                                <td className="tw-text-end">{$t('Grand Total')}</td>
                                <td className="inbud-numeric-cell">{money(grand.projects_total)}</td>
                                <td className="inbud-numeric-cell">{money(grand.total)}</td>
                                <td className="inbud-numeric-cell">
                                    {grand.unallocated_percent === null || grand.unallocated_percent === undefined
                                        ? MARGIN_NO_DIVISOR
                                        : percentage(grand.unallocated_percent)}
                                </td>
                            </tr>
                        </tfoot>
                    )}
                </table>
            </div>
        </div>
    );
}
